import React from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "../styles/ReactQuillEditor.css";

/**
 * ReactQuillEditor - A reusable rich text editor component compatible with Ant Design Form
 * 
 * @param {string} value - The HTML content value
 * @param {function} onChange - Callback function when content changes
 * @param {string} placeholder - Placeholder text
 * @param {number} height - Fixed height of the editor in pixels (default: 200)
 * @param {boolean} showToolbar - Whether to show the toolbar (default: true)
 * @param {array} toolbarOptions - Custom toolbar configuration
 * @param {object} style - Additional inline styles
 * @param {boolean} readOnly - Whether the editor is read-only
 * @param {object} modules - Custom Quill modules configuration
 * @param {array} formats - Custom formats array
 * @param {string} theme - Quill theme ('snow' or 'bubble', default: 'snow')
 */
const ReactQuillEditor = ({
  value,
  onChange,
  placeholder = "Enter text...",
  height = 400,
  showToolbar = true,
  toolbarOptions = null,
  style = {},
  readOnly = false,
  modules = null,
  formats = null,
  theme = "snow",
  ...props
}) => {
  const handleChange = (content) => {
    if (onChange) {
      onChange(content);
    }
  };

  // Default toolbar configuration
  const defaultToolbar = [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    [{ script: "sub" }, { script: "super" }],
    [{ direction: "rtl" }],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    ["link", "image", "video"],
    ["clean"],
  ];

  // Default formats
  const defaultFormats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "script",
    "direction",
    "color",
    "background",
    "align",
    "link",
    "image",
    "video",
  ];

  // Default modules configuration
  const defaultModules = {
    toolbar: showToolbar ? (toolbarOptions || defaultToolbar) : false,
    clipboard: {
      matchVisual: false,
    },
  };

  return (
    <div className="react-quill-wrapper" style={style}>
      <ReactQuill
        theme={theme}
        value={value || ""}
        onChange={handleChange}
        placeholder={placeholder}
        readOnly={readOnly}
        modules={modules || defaultModules}
        formats={formats || defaultFormats}
        style={{
          backgroundColor: "#fff",
          borderRadius: "8px",
        }}
        {...props}
      />
      <style jsx global>{`
        .react-quill-wrapper .ql-container {
          height: ${height}px;
          border-bottom-left-radius: 8px;
          border-bottom-right-radius: 8px;
          font-size: 14px;
          overflow-y: auto;
          overflow-x: hidden;
        }
        .react-quill-wrapper .ql-toolbar {
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;
          border-bottom: 1px solid #d9d9d9;
          background-color: #fafafa;
        }
        .react-quill-wrapper .ql-editor {
          min-height: ${height}px;
          height: 100%;
          overflow-y: auto;
        }
        .react-quill-wrapper .ql-editor.ql-blank::before {
          color: rgba(0, 0, 0, 0.25);
          font-style: normal;
        }
        .react-quill-wrapper .ql-editor p {
          margin-bottom: 0.5em;
        }
        .react-quill-wrapper .ql-editor.ql-blank::before {
          left: 12px;
          right: 12px;
        }
        .react-quill-wrapper .ql-snow .ql-stroke {
          stroke: #595959;
        }
        .react-quill-wrapper .ql-snow .ql-fill {
          fill: #595959;
        }
        .react-quill-wrapper .ql-snow .ql-picker-label {
          color: #595959;
        }
        .react-quill-wrapper .ql-snow.ql-toolbar button:hover,
        .react-quill-wrapper .ql-snow .ql-toolbar button:hover,
        .react-quill-wrapper .ql-snow.ql-toolbar button.ql-active,
        .react-quill-wrapper .ql-snow .ql-toolbar button.ql-active {
          color: #1890ff;
        }
        .react-quill-wrapper .ql-snow.ql-toolbar button:hover .ql-stroke,
        .react-quill-wrapper .ql-snow .ql-toolbar button:hover .ql-stroke,
        .react-quill-wrapper .ql-snow.ql-toolbar button.ql-active .ql-stroke,
        .react-quill-wrapper .ql-snow .ql-toolbar button.ql-active .ql-stroke {
          stroke: #1890ff;
        }
        .react-quill-wrapper .ql-snow.ql-toolbar button:hover .ql-fill,
        .react-quill-wrapper .ql-snow .ql-toolbar button:hover .ql-fill,
        .react-quill-wrapper .ql-snow.ql-toolbar button.ql-active .ql-fill,
        .react-quill-wrapper .ql-snow .ql-toolbar button.ql-active .ql-fill {
          fill: #1890ff;
        }
      `}</style>
    </div>
  );
};

export default ReactQuillEditor